<script setup lang="ts">
  import { newsApi } from '@/api/index'
  import type { Protocol } from '@xianlaibang/shared/types'

  const route = useRoute()
  const id = computed(() => route.params.id as string)

  // 侧边栏菜单
  const activeMenu = computed(() => Number(id.value))
  const activeItem = ref<Protocol>({} as Protocol)
  const protocolList = ref<Protocol[]>()

  async function loadProtocolData() {
    if (!protocolList.value?.length) {
      const protocolRes = await newsApi.getProtocol()
      protocolList.value = protocolRes.data || []
    }
    activeItem.value = protocolList.value.find(
      item => item.platformProtocolId === Number(id.value),
    ) || {} as Protocol
  }

  onMounted(loadProtocolData)
  watch(id, loadProtocolData)
</script>

<template>
  <div class="notice-page">
    <div class="container">
      <!-- 面包屑 -->
      <div class="breadcrumb">
        <NuxtLink to="/">首页</NuxtLink> &gt; <span class="current">{{ activeItem.protocolName }}</span>
      </div>

      <div class="main-layout">
        <!-- 左侧侧边栏 -->
        <div class="sidebar">
          <div class="sidebar-header">
            内容中心
          </div>
          <ul class="sidebar-menu">
            <li :class="{ active: activeMenu === 0 }">
              <NuxtLink to="/notice">全部公告</NuxtLink>
            </li>
            <li
              v-for="item in protocolList"
              :key="item.platformProtocolId"
              :class="{ active: activeMenu === item.platformProtocolId }"
            >
              <NuxtLink :to="`/notice/${item.platformProtocolId}`">
                {{ item.protocolName }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <!-- 右侧内容区 -->
        <div class="content-area">
          <div>
            <span v-html="activeItem.noticeContent"></span>
            <NuxtPage />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notice-page {
  background-color: #F5F7FA;
  /* min-height: calc(100vh - 80px); 减去头部高度 */
  padding: 20px 0;
  color: #333;
}

.container {
  width: 1500px;
  margin: 0 auto;
  max-width: 100%;
  padding: 0 20px;
}

.breadcrumb {
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;

  a {
    color: #666;
    text-decoration: none;
    &:hover {
      color: #FF7A3C;
    }
  }

  .current {
    color: #333;
    font-weight: 500;
  }
}

.main-layout {
  display: flex;
  gap: 20px;
}

/* 左侧侧边栏 */
.sidebar {
  width: 280px;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  flex-shrink: 0;

  .sidebar-header {
    height: 60px;
    line-height: 60px;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    border-bottom: 1px solid #f0f0f0;
  }

  .sidebar-menu {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      height: 50px;
      line-height: 50px;
      text-align: center;
      cursor: pointer;
      font-size: 18px;
      color: #0E2036;
      transition: all 0.3s;

      a {
        display: block;
        height: 100%;
        line-height: 50px;
        color: inherit;
      }

      &:hover {
        color: #FF7A3C;
        background-color: #ecf5ff;
      }

      &.active {
        color: #FF7A3C;
        background-color: #ecf5ff;
        font-weight: 500;
      }
    }
  }
}

/* 右侧内容区 */
.content-area {
  flex: 1;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  min-height: 600px;
  padding: 20px;

  .card-header {
    margin-bottom: 20px;
    
    .tab-pill {
      display: inline-block;
      padding: 6px 20px;
      background: #E6F1FC;
      color: #FF7A3C;
      border-radius: 20px;
      font-size: 14px;
      font-weight: bold;
    }
  }

  .notice-list {
    min-height: 400px;
  }

  .notice-item {
    border-bottom: 1px dashed #eee;
    padding: 20px 0;
    
    &:last-child {
      border-bottom: none;
    }

    .item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      
      .title-left {
        display: flex;
        align-items: center;
        font-size: 15px;
        color: #333;
        font-weight: 500;
        
        .bell-icon {
          color: #FF7A3C;
          font-size: 20px;
          margin-right: 10px;
        }
      }

      .date {
        color: #999;
        font-size: 14px;
      }
    }

    .item-body {
      margin-top: 15px;
      padding-left: 30px; /* 对齐图标右侧 */
      
      .content-text {
        color: #666;
        font-size: 14px;
        line-height: 1.8;
        
        pre {
          white-space: pre-wrap;
          font-family: inherit;
          margin: 0;
        }
      }
    }

    .action-row {
      display: flex;
      justify-content: flex-end;
      margin-top: 10px;
      
      &.collapsed {
        margin-top: 5px;
      }

      .action-btn {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 13px;
        color: #666;
        
        &:hover {
          color: #FF7A3C;
        }

        .el-icon {
          margin-left: 4px;
        }
      }
    }
  }
}

/* 分页 */
.pagination-container {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;

  :deep(.el-pagination) {
    --el-pagination-button-bg-color: #f4f4f5;
    
    .el-pager li {
      background: #f4f4f5;
      font-weight: normal;
      &.is-active {
        background-color: #FF7A3C;
        color: #fff;
      }
    }
  }

  .pagination-append {
    display: flex;
    align-items: center;
    margin-left: 10px;
    font-size: 13px;
    color: #606266;

    .page-count {
      margin-right: 10px;
    }

    .text {
      margin: 0 5px;
    }

    .jump-input {
      width: 40px;
      
      :deep(.el-input__wrapper) {
        padding: 0 5px;
      }
      
      :deep(.el-input__inner) {
        text-align: center;
        height: 28px;
        line-height: 28px;
      }
    }

    .jump-btn {
      margin-left: 10px;
      height: 28px;
      padding: 0 15px;
      border-radius: 14px; /* 圆角按钮 */
      font-size: 12px;
    }
  }
}
</style>
